import { addressPartsMatch } from 'utils/address/addressPartsMatch';

import { shouldRunPhoneReport } from '../../featureFlags';
import { isWithinMonths, toJsDate, yearsSince } from '../dateWindow';
import { findSubjectListing } from '../findSubjectListing';

import {
  BANKRUPTCY_LOOKBACK_MONTHS,
  FAILED_LISTING_LOOKBACK_MONTHS,
  HIGH_EQUITY_MAX_ALTV,
  LONG_TENURE_MIN_YEARS,
  PREFORECLOSURE_LOOKBACK_MONTHS,
  RECENTLY_MOVED_LOOKBACK_MONTHS,
  RESOLVED_CIVIL_STATUSES,
  fired,
  isFamilyTransfer,
  mostRecentDeed,
  notFired,
  unavailable,
} from './motivatedSellerHelpers';

import type { LeadScoreConfig } from '../types';

export const motivatedSellerConfig: LeadScoreConfig = {
  configId: 'motivated-seller',

  signals: {
    preforeclosure: {
      category: 'pressure',
      label: 'Preforeclosure',
      evaluate: (ctx) => {
        const pfc = ctx.subjectProperty.preforeclosure;
        const parsed = ctx.subjectProperty.parcel_address?.parsed;
        const canMatchSubject = Boolean(
          parsed?.primary_address_number?.trim() &&
            parsed?.zip5?.trim() &&
            parsed?.street_name?.trim()
        );
        if (!pfc) {
          if (!canMatchSubject) {
            return unavailable('No preforeclosure records available');
          }
          return notFired('No current foreclosure activity found');
        }
        const recentEvent = (pfc.events ?? []).find((event) =>
          isWithinMonths(
            event.recording_date,
            PREFORECLOSURE_LOOKBACK_MONTHS,
            ctx.now
          )
        );
        if (!recentEvent) {
          return notFired('No current foreclosure activity found');
        }
        const year = recentEvent.recording_date?.parsed?.year;
        return fired(
          year != null
            ? `Foreclosure filing on record (${year})`
            : 'Foreclosure filing on record'
        );
      },
    },

    tax_delinquency: {
      category: 'pressure',
      label: 'Tax Delinquency',
      evaluate: (ctx) => {
        const bills = ctx.subjectProperty.bills;
        if (bills == null) {
          return unavailable('No tax records available');
        }
        if (!bills.length) {
          return notFired('No current tax delinquency found');
        }
        const mostRecent = [...bills].sort(
          (a, b) => (b.year ?? 0) - (a.year ?? 0)
        )[0];
        if (mostRecent?.delinquent_year != null) {
          return fired(`Tax delinquent since ${mostRecent.delinquent_year}`);
        }
        return notFired('No current tax delinquency found');
      },
    },

    lien_active: {
      category: 'pressure',
      label: 'Lien Active',
      evaluate: (ctx) => {
        const propertyCivils = ctx.subjectProperty.courts?.civils;
        const personCivils = ctx.personCourts?.civil;
        const activeLiens = [
          ...(propertyCivils ?? []),
          ...(personCivils ?? []),
        ].filter((civil) => {
          if (
            civil.status &&
            RESOLVED_CIVIL_STATUSES.has(civil.status.toLowerCase())
          ) {
            return false;
          }
          const filings = civil.filings ?? [];
          if (!filings.length) {
            return false;
          }
          const hasRelease = filings.some(
            (filing) =>
              filing.type && /RELEASE|SATISF|DISMISS/i.test(filing.type)
          );
          return !hasRelease;
        });
        if (!activeLiens.length) {
          if (propertyCivils == null || personCivils == null) {
            return unavailable('No lien records available');
          }
          return notFired('No open liens found');
        }
        const lienYears = activeLiens
          .flatMap((civil) => [
            civil.filing_date?.parsed?.year,
            ...(civil.filings ?? []).map(
              (filing) => filing.filing_date?.parsed?.year
            ),
          ])
          .filter((year): year is number => year != null);
        const mostRecentYear = lienYears.length
          ? Math.max(...lienYears)
          : undefined;
        if (activeLiens.length === 1) {
          return fired(
            mostRecentYear != null
              ? `Lien found (${mostRecentYear})`
              : 'Lien found'
          );
        }
        return fired(
          mostRecentYear != null
            ? `${activeLiens.length} liens found, most recent ${mostRecentYear}`
            : `${activeLiens.length} liens found`
        );
      },
    },

    bankruptcy_filed: {
      category: 'pressure',
      label: 'Bankruptcy filing',
      evaluate: (ctx) => {
        const bankruptcies = ctx.personCourts?.bankruptcy;
        if (!bankruptcies) {
          return unavailable('No bankruptcy records available');
        }
        const relevantFiling = bankruptcies.find(
          (bankruptcy) =>
            !bankruptcy.discharge_date ||
            isWithinMonths(
              bankruptcy.filing_date,
              BANKRUPTCY_LOOKBACK_MONTHS,
              ctx.now
            )
        );
        if (!relevantFiling) {
          return notFired();
        }
        const evidence = relevantFiling.filing_date?.full
          ? `Filed ${relevantFiling.filing_date.full}`
          : undefined;
        return fired(evidence);
      },
    },

    failed_listing: {
      category: 'pressure',
      label: 'Failed Listing',
      evaluate: (ctx) => {
        const newestSubjectListing = [
          ...(ctx.subjectProperty.listings ?? []),
        ].sort(
          (a, b) =>
            (toJsDate(b.initial_list_date)?.getTime() ?? 0) -
            (toJsDate(a.initial_list_date)?.getTime() ?? 0)
        )[0];
        const listing =
          newestSubjectListing ??
          findSubjectListing(ctx.reportListings, ctx.subjectProperty);

        if (
          ctx.subjectProperty.listings == null &&
          ctx.reportListings == null
        ) {
          return unavailable('No listing records available');
        }
        if (!listing) {
          return notFired('No recent failed listings found');
        }

        const isFailed = listing.status === 'FAIL';
        const isRecent = isWithinMonths(
          listing.initial_list_date,
          FAILED_LISTING_LOOKBACK_MONTHS,
          ctx.now
        );
        if (!isFailed || !isRecent) {
          return notFired('No recent failed listings found');
        }
        const year = listing.initial_list_date?.parsed?.year;
        return fired(
          year != null
            ? `Listed but not sold in ${year}`
            : 'Listed but not sold'
        );
      },
    },

    recently_moved: {
      category: 'pressure',
      label: 'Recently Moved',
      evaluate: (ctx) => {
        if (!ctx.personAddresses) {
          return unavailable('No address history available');
        }
        const bestAddress = ctx.personAddresses.find(
          (address) => address?.meta?.is_best
        );
        if (
          !bestAddress?.parsed ||
          !ctx.subjectProperty.parcel_address?.parsed
        ) {
          return unavailable('Address could not be compared');
        }

        const movedAway = !addressPartsMatch(
          ctx.subjectProperty.parcel_address.parsed,
          bestAddress.parsed
        );
        const isRecent = isWithinMonths(
          bestAddress.meta?.first_seen_date,
          RECENTLY_MOVED_LOOKBACK_MONTHS,
          ctx.now
        );
        return movedAway && isRecent ? fired() : notFired();
      },
    },

    deceased_owner: {
      category: 'pressure',
      label: 'Deceased Owner',
      evaluate: (ctx) => {
        if (!ctx.identity) {
          return unavailable('No owner identity available');
        }
        if (!ctx.identity.is_deceased) {
          return notFired('No');
        }
        return fired('Yes');
      },
    },

    family_transfer: {
      category: 'pressure',
      label: 'Family Transfer',
      evaluate: (ctx) => {
        if (ctx.subjectProperty.deeds == null) {
          return unavailable('No deed records available');
        }
        const deed = mostRecentDeed(ctx);
        if (!deed || !isFamilyTransfer(deed, ctx.now)) {
          return notFired('No recent family transfer found.');
        }
        const year = deed.transfer?.filing_date?.parsed?.year;
        return fired(
          year != null
            ? `Transferred between related parties (${year})`
            : 'Transferred between related parties'
        );
      },
    },

    absentee_owner: {
      category: 'context',
      label: 'Absentee Owner',
      evaluate: (ctx) => {
        const ownerOccupied = ctx.subjectProperty.owner_occupied;
        if (ownerOccupied == null) {
          return unavailable('No occupancy data available');
        }
        if (ownerOccupied) {
          return notFired('No, property is owner occupied');
        }
        const bestAddress = ctx.personAddresses?.find(
          (address) => address?.meta?.is_best
        );
        return fired(
          bestAddress?.full ? `Yes, mail goes to ${bestAddress.full}` : 'Yes'
        );
      },
    },

    long_tenure: {
      category: 'context',
      label: 'Tenure',
      evaluate: (ctx) => {
        const lastSoldDate = ctx.subjectProperty.last_sold_date;
        if (!lastSoldDate) {
          return unavailable('No sale date available');
        }
        const deed = mostRecentDeed(ctx);
        if (deed && isFamilyTransfer(deed, ctx.now)) {
          return notFired();
        }
        const years = yearsSince(lastSoldDate, ctx.now);
        const evidence =
          years != null
            ? `Property owned for ${years.toFixed(1)} years`
            : undefined;
        return years != null && years >= LONG_TENURE_MIN_YEARS
          ? fired(evidence)
          : notFired(evidence);
      },
    },

    high_equity: {
      category: 'context',
      label: 'Equity',
      evaluate: (ctx) => {
        const altv = ctx.subjectProperty.equities?.[0]?.altv;
        if (altv == null) {
          return unavailable('No equity data available');
        }
        const estimatedEquity =
          ctx.subjectProperty.equities?.[0]?.estimated_available_equity;
        const evidence =
          estimatedEquity != null
            ? `Estimated equity of $${Math.round(
                estimatedEquity
              ).toLocaleString('en-US')}`
            : undefined;
        return altv < HIGH_EQUITY_MAX_ALTV
          ? fired(evidence)
          : notFired(evidence);
      },
    },
  },

  baseThresholds: [
    { count: 5, base: 100 },
    { count: 3, base: 75 },
    { count: 2, base: 50 },
    { count: 1, base: 25 },
    { count: 0, base: 0 },
  ],

  pressureGate: { minBase: 75, requiresPressure: true, gatedBase: 50 },

  contactabilityModifier: (input) => {
    if (!shouldRunPhoneReport) {
      return 1.0;
    }
    if (input.isDoNotCall) {
      return 0.5;
    }
    if (
      input.isActive &&
      input.userActivity != null &&
      ['high', 'medium'].includes(input.userActivity.toLowerCase())
    ) {
      return 1.0;
    }
    if (input.isActive || input.hasEmail || !input.hasPhoneReport) {
      return 0.75;
    }
    return 0.5;
  },

  tierThresholds: [
    { score: 80, tier: 'high' },
    { score: 50, tier: 'moderate' },
    { score: 0, tier: 'low' },
  ],

  actionByTier: {
    high: 'call_today',
    moderate: 'follow_up_this_week',
    low: 'monitor',
  },

  reasonText: {
    preforeclosure: {
      segments: [
        {
          text: 'A preforeclosure filing was recorded on this property, indicating financial pressure that often precedes a sale.',
        },
      ],
    },
    tax_delinquency: {
      segments: [
        {
          text: 'This property has delinquent property taxes, which can be a strong motivator to sell.',
        },
      ],
    },
    lien_active: {
      segments: [
        {
          text: 'An active lien or judgment was found against this property or its owner.',
        },
      ],
    },
    bankruptcy_filed: {
      segments: [
        {
          text: 'A recent or open bankruptcy filing was found for the property owner.',
        },
      ],
    },
    failed_listing: {
      segments: [
        {
          text: 'A previous listing attempt for this property did not result in a sale.',
        },
      ],
    },
    recently_moved: {
      segments: [
        {
          text: 'The property owner appears to have moved away from this address.',
        },
      ],
    },
    deceased_owner: {
      segments: [{ text: 'Records indicate the property owner is deceased.' }],
    },
    family_transfer: {
      segments: [
        {
          text: 'This property was recently transferred between family members.',
        },
      ],
    },
    absentee_owner: {
      segments: [
        { text: 'The owner does not appear to occupy this property.' },
      ],
    },
    long_tenure: {
      segments: [
        {
          text: 'The current owner has held this property for an extended period, increasing the likelihood of built-up equity.',
        },
      ],
    },
    high_equity: {
      segments: [
        {
          text: 'This property appears to have significant equity relative to its value.',
        },
      ],
    },
  },

  copyByTier: {
    high: {
      headline: 'High-priority lead',
      description:
        'Multiple pressure signals suggest this homeowner may be motivated to sell soon.',
    },
    moderate: {
      headline: 'Worth monitoring',
      description:
        'Some motivation signals are present. Consider following up this week.',
    },
    low: {
      headline: 'Limited motivation signals',
      description:
        'Few indicators suggest an imminent sale, but keep an eye on this lead.',
    },
  },
};
